"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { getAssetPath, getBrandConfig, withDocsSrc } from "@/lib/brand";
import { useIsSspChannel } from "@/hooks/use-ssp-channel";
import { Link } from "@/i18n/navigation";

export function Footer() {
  const t = useTranslations("footer");
  const { theme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentTheme = theme === "system" ? resolvedTheme : theme;
  const brand = getBrandConfig();
  const isSspChannel = useIsSspChannel();
  const docsUrl = withDocsSrc(brand.headerLinks.docs, isSspChannel);
  const logoSrc =
    currentTheme === "dark"
      ? getAssetPath(brand.logoDark)
      : getAssetPath(brand.logoLight);

  const productLinks = [
    { key: "modelHub", href: "/models", external: false },
    { key: "console", href: "/platform/account", external: false },
    { key: "apiKeys", href: "/platform/keys", external: false },
    { key: "usageQuery", href: "/platform/account/usage", external: false },
    ...(docsUrl
      ? [{ key: "docs", href: docsUrl, external: true } as const]
      : []),
  ] as const;
  const resourceLinks = [
    "quickstart",
    "docs",
    "sdk",
    "practices",
    "cases",
    "blog",
  ] as const;
  const supportLinks = [
    "help",
    "community",
    "contact",
    "status",
    "feedback",
  ] as const;

  return (
    <footer className="border-t border-border/60 bg-gradient-to-b from-background to-muted/20">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:py-14 lg:py-16">
        {/* 主要内容区 */}
        <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-start lg:gap-12">
          {/* 品牌区域 */}
          <div className="space-y-3 text-center md:text-left">
            <div className="flex items-center justify-center gap-3 md:justify-start">
              {mounted && (
                <Image
                  src={logoSrc}
                  alt={t("brand")}
                  width={40}
                  height={40}
                  className="h-9 w-9 sm:h-10 sm:w-10"
                />
              )}
              <div className="text-xl font-bold sm:text-2xl">{t("brand")}</div>
            </div>
            <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground md:mx-0">
              {t("slogan")}
            </p>
          </div>

          {/* 产品 */}
          <div className="text-center md:text-right">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground md:mb-4">
              {t("product")}
            </div>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm md:flex-col md:items-end md:gap-3">
              {productLinks.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  {...(item.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className="py-1 text-muted-foreground transition hover:text-primary hover:underline md:py-0"
                >
                  {t(`productLinks.${item.key}`)}
                </Link>
              ))}
            </div>
          </div>

          {/* 资源 */}
          {/* <div className="text-right">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wide text-foreground">
              {t("resources")}
            </div>
            <div className="flex flex-col gap-3 text-sm">
              {resourceLinks.map((key) => (
                <Link
                  key={key}
                  href={`#${key}`}
                  className="text-muted-foreground transition hover:text-primary hover:underline"
                >
                  {t(`resourceLinks.${key}`)}
                </Link>
              ))}
            </div>
          </div> */}

          {/* 支持 */}
          {/* <div className="text-right">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wide text-foreground">
              {t("support")}
            </div>
            <div className="flex flex-col gap-3 text-sm">
              {supportLinks.map((key) => (
                <Link
                  key={key}
                  href={`#${key}`}
                  className="text-muted-foreground transition hover:text-primary hover:underline"
                >
                  {t(`supportLinks.${key}`)}
                </Link>
              ))}
            </div>
          </div> */}
        </div>

        {/* 底部信息栏 */}
        <div className="mt-8 border-t border-border/60 pt-5 sm:mt-10 sm:pt-6 lg:mt-12 lg:pt-8">
          <div className="flex flex-col items-center justify-between gap-4 text-center text-xs text-muted-foreground sm:flex-row">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:justify-start">
              <span className="w-full sm:w-auto">{t("copyright")}</span>
              {/* <span className="max-sm:hidden sm:inline">|</span> */}
              {/* <Link
                href="#icp"
                className="hover:text-foreground hover:underline"
              >
                {t("icp")}
              </Link> */}
              <span className="max-sm:hidden sm:inline">|</span>
              <Link
                href="/privacy"
                className="hover:text-foreground hover:underline"
              >
                {t("privacy")}
              </Link>
              <span className="max-sm:hidden sm:inline">|</span>
              <Link
                href="/terms"
                className="hover:text-foreground hover:underline"
              >
                {t("terms")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
